import { DataProvider } from "react-admin";
import { API } from "./api";
import { startLoading, stopLoading } from "./loading";

interface SpringPage<T> {
  content: T[];
  totalElements: number;
}

const resourceConfig: Record<string, { path: string; idField: string }> = {
  "db-rounds": { path: "/db/rounds", idField: "roundId" },
  "db-bets": { path: "/db/bets", idField: "betId" },
  "db-wallet-requests": { path: "/db/wallet-requests", idField: "id" },
  "db-game-configs": { path: "/db/game-configs", idField: "id" },
  "db-operators": { path: "/db/operators", idField: "operatorId" },
  "db-users": { path: "/db/users", idField: "id" },
};

function addId<T extends Record<string, unknown>>(
  record: T,
  idField: string,
): T & { id: unknown } {
  return { ...record, id: record[idField] };
}

async function trackedFetch(url: string): Promise<Response> {
  startLoading();
  try {
    return await fetch(url);
  } finally {
    stopLoading();
  }
}

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const config = resourceConfig[resource];
    if (!config) throw new Error(`Unknown resource: ${resource}`);

    const { page = 1, perPage = 50 } = params.pagination ?? {};
    const filter = params.filter ?? {};

    const url = new URL(`${API}${config.path}`);
    url.searchParams.set("page", String(page - 1));
    url.searchParams.set("size", String(perPage));

    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }

    const res = await trackedFetch(url.toString());
    if (!res.ok) throw new Error(`API error ${res.status}`);

    const text = await res.text();
    if (!text || text === "null") return { data: [], total: 0 };
    const json = JSON.parse(text);
    if (Array.isArray(json)) {
      return {
        data: json.map((r: Record<string, unknown>) =>
          addId(r, config.idField),
        ),
        total: json.length,
      };
    }

    const springPage = json as SpringPage<Record<string, unknown>>;
    return {
      data: springPage.content.map((r) => addId(r, config.idField)),
      total: springPage.totalElements,
    };
  },

  getOne: async (resource, params) => {
    const config = resourceConfig[resource];
    if (!config) throw new Error(`Unknown resource: ${resource}`);

    const res = await trackedFetch(`${API}${config.path}/${params.id}`);
    if (!res.ok) throw new Error(`API error ${res.status}`);
    const text = await res.text();
    if (!text || text === "null")
      throw new Error(`Resource not found: ${params.id}`);
    const json = JSON.parse(text);
    return { data: addId(json, config.idField) };
  },

  getMany: async (resource, params) => {
    const config = resourceConfig[resource];
    if (!config) throw new Error(`Unknown resource: ${resource}`);

    const results = await Promise.all(
      params.ids.map(async (id) => {
        const res = await trackedFetch(`${API}${config.path}/${id}`);
        if (!res.ok) return null;
        const text = await res.text();
        if (!text || text === "null") return null;
        const json = JSON.parse(text);
        return addId(json, config.idField);
      }),
    );
    return { data: results.filter(Boolean) };
  },

  getManyReference: async () => ({ data: [], total: 0 }),
  create: async () => ({ data: { id: "" } }),
  update: async () => ({ data: { id: "" } }),
  updateMany: async () => ({ data: [] }),
  delete: async () => ({ data: { id: "" } }),
  deleteMany: async () => ({ data: [] }),
};
